// Pure helpers of the ontology sync page (spec 2026-09-25-02). The server
// computes the diff; this module only indexes it, keeps the selection closed
// over the `new` dependencies, and filters what the page shows.

import type { components } from '../api/schema'

export type SyncDiff = components['schemas']['OntologySyncDiff']
export type SyncFamilyDiff = components['schemas']['OntologySyncFamilyDiff']
export type SyncDiffItem = components['schemas']['OntologySyncDiffItem']
export type SyncChange = components['schemas']['OntologySyncChange']
export type SyncItemRef = components['schemas']['OntologySyncItemRef']
export type SyncFamily = components['schemas']['OntologySyncFamily']
export type SyncStatus = components['schemas']['OntologySyncStatus']
export type SyncOutcome = components['schemas']['OntologySyncOutcome']
export type SyncOutcomeItem = components['schemas']['OntologySyncOutcomeItem']
export type SyncRun = components['schemas']['OntologySyncRun']
export type SyncRunSummary = components['schemas']['OntologySyncRunSummary']
export type SyncSelectionItem = components['schemas']['OntologySyncSelectionItem']
export type SyncMissingDependency = components['schemas']['OntologySyncMissingDependency']
export type SyncSourceKind = 'template' | 'defaults'
export type SyncDrift = components['schemas']['OntologySyncDrift']
export type SyncDriftDocumentType = components['schemas']['OntologySyncDriftDocumentType']
export type SyncReextraction = components['schemas']['OntologySyncReextraction']

/** The 18 families, in import order: the order of the family list. */
export const SYNC_FAMILIES: SyncFamily[] = [
  'property_categories',
  'properties',
  'asset_types',
  'property_profiles',
  'asset_relation_types',
  'document_types',
  'folder_layouts',
  'contract_types',
  'contract_stakeholder_types',
  'derivations',
  'normalizations',
  'aggregations',
  'compliance_controls',
  'compliance_rules',
  'property_mapping_rules',
  'document_expectations',
  'tags',
  'sources',
  'thematics',
]

/** Statuses in the order the family panel groups them. */
export const SYNC_STATUSES: SyncStatus[] = ['new', 'different', 'local_only', 'identical', 'unsyncable']

/** A selectable item: only what the source has and the target lacks or differs on. */
export function isSelectable(item: Pick<SyncDiffItem, 'status'>): boolean {
  return item.status === 'new' || item.status === 'different'
}

export function refId(family: string, key: string): string {
  return `${family}\u0000${key}`
}

export function parseRefId(id: string): SyncItemRef {
  const i = id.indexOf('\u0000')
  return { family: id.slice(0, i) as SyncFamily, key: id.slice(i + 1) }
}

export interface IndexedItem {
  family: SyncFamily
  item: SyncDiffItem
}

export interface SyncIndex {
  items: Map<string, IndexedItem>
  /** id → ids of the items that require it (reverse of `requires`). */
  neededBy: Map<string, string[]>
}

export function indexDiff(diff: SyncDiff | null | undefined): SyncIndex {
  const items = new Map<string, IndexedItem>()
  const neededBy = new Map<string, string[]>()
  for (const f of diff?.families ?? []) {
    for (const item of f.items) {
      const id = refId(f.family, item.key)
      items.set(id, { family: f.family, item })
      for (const dep of item.requires ?? []) {
        const depId = refId(dep.family, dep.key)
        const list = neededBy.get(depId) ?? []
        list.push(id)
        neededBy.set(depId, list)
      }
    }
  }
  return { items, neededBy }
}

/**
 * The selection: id → the id of the item whose check pulled it in, or `null`
 * when the admin chose it. The server refuses a selection missing a `new`
 * dependency, so the page never builds one.
 */
export type Selection = Map<string, string | null>

/** Checks items, and recursively their `new` dependencies. */
export function selectItems(selection: Selection, ids: string[], index: SyncIndex): Selection {
  const next = new Map(selection)
  const visit = (id: string, pulledBy: string | null) => {
    const entry = index.items.get(id)
    if (!entry || !isSelectable(entry.item)) return
    if (next.has(id)) {
      // Chosen directly now: it is no longer "required by" anything.
      if (pulledBy === null) next.set(id, null)
      return
    }
    next.set(id, pulledBy)
    for (const dep of entry.item.requires ?? []) {
      visit(refId(dep.family, dep.key), id)
    }
  }
  for (const id of ids) visit(id, null)
  return next
}

/**
 * Unchecks items, and every selected item that needs one of them
 * (transitively). Returns what else was unchecked, for the notice.
 */
export function unselectItems(
  selection: Selection,
  ids: string[],
  index: SyncIndex,
): { selection: Selection; alsoUnchecked: string[] } {
  const next = new Map(selection)
  const alsoUnchecked: string[] = []
  const direct = new Set(ids)
  const visit = (id: string) => {
    if (!next.has(id)) return
    next.delete(id)
    if (!direct.has(id)) alsoUnchecked.push(id)
    for (const needer of index.neededBy.get(id) ?? []) visit(needer)
  }
  for (const id of ids) visit(id)
  // A dependency pulled in by an item that is gone stays selected but is now
  // the admin's own choice.
  for (const [id, pulledBy] of next) {
    if (pulledBy !== null && !next.has(pulledBy)) next.set(id, null)
  }
  return { selection: next, alsoUnchecked }
}

/** The request body items for a selection. `resetTuning` holds the ids of the
 * tuned compliance rules the admin brings back to their shipped values
 * ("Revenir aux valeurs livrées", spec 2026-10-27-21 § 4); the others keep
 * the organization's tuning. */
export function selectionPayload(
  selection: Selection,
  index: SyncIndex,
  resetTuning: ReadonlySet<string> = new Set(),
): SyncSelectionItem[] {
  const out: SyncSelectionItem[] = []
  for (const id of selection.keys()) {
    const entry = index.items.get(id)
    if (!entry) continue
    out.push({
      family: entry.family,
      key: entry.item.key,
      source_hash: entry.item.source_hash,
      target_hash: entry.item.target_hash,
      ...(resetTuning.has(id) && isTuned(entry.item) ? { reset_tuning: true } : {}),
    })
  }
  return out
}

/** A compliance rule the organization tuned: the sync keeps its tuning. */
export function isTuned(item: Pick<SyncDiffItem, 'flags'>): boolean {
  return (item.flags ?? []).includes('tuned')
}

/**
 * After the diff reloads (a 409), keep the selected items that are still
 * eligible and close the selection again over the fresh dependencies.
 */
export function keepEligible(selection: Selection, index: SyncIndex): Selection {
  const kept: Selection = new Map()
  for (const [id] of selection) {
    const entry = index.items.get(id)
    if (entry && isSelectable(entry.item)) kept.set(id, null)
  }
  return selectItems(kept, [...kept.keys()], index)
}

export type CheckState = 'checked' | 'unchecked' | 'indeterminate'

/** The tri-state of a family's checkbox, over its selectable items. */
export function familyCheckState(family: SyncFamilyDiff, selection: Selection): CheckState {
  let selected = 0
  let selectable = 0
  for (const item of family.items) {
    if (!isSelectable(item)) continue
    selectable++
    if (selection.has(refId(family.family, item.key))) selected++
  }
  if (selectable === 0 || selected === 0) return 'unchecked'
  return selected === selectable ? 'checked' : 'indeterminate'
}

export function selectableIds(family: SyncFamilyDiff): string[] {
  return family.items.filter(isSelectable).map((item) => refId(family.family, item.key))
}

/** Every `new` item, all families: "Select all new" and "Review all". */
export function allNewIds(diff: SyncDiff | null | undefined): string[] {
  const out: string[] = []
  for (const f of diff?.families ?? []) {
    for (const item of f.items) if (item.status === 'new') out.push(refId(f.family, item.key))
  }
  return out
}

/** `status=new,different` → the statuses; absent or empty → every status. */
export function parseStatuses(raw: string | undefined | null): SyncStatus[] {
  if (!raw) return [...SYNC_STATUSES]
  const wanted = raw.split(',').map((s) => s.trim())
  const out = SYNC_STATUSES.filter((s) => wanted.includes(s))
  return out.length > 0 ? out : [...SYNC_STATUSES]
}

export function localizedLabel(label: Record<string, string> | undefined | null, locale: string): string {
  if (!label) return ''
  return label[locale] || label.en || label.fr || Object.values(label)[0] || ''
}

/** Search on the key and on every locale of the label. */
export function matchesQuery(item: SyncDiffItem, q: string | undefined | null): boolean {
  const needle = (q ?? '').trim().toLowerCase()
  if (!needle) return true
  if (item.key.toLowerCase().includes(needle)) return true
  return Object.values(item.label ?? {}).some((v) => v.toLowerCase().includes(needle))
}

/**
 * The organization holds no item of any family: the page opens on the
 * first-sync panel.
 */
export function isEmptyTarget(diff: SyncDiff | null | undefined): boolean {
  if (!diff) return false
  const totals = diff.totals ?? {}
  const held = (totals.different ?? 0) + (totals.identical ?? 0) + (totals.local_only ?? 0)
  return held === 0 && (totals.new ?? 0) > 0
}

/** The family the panel opens on: the URL's, else the first with work. */
export function defaultFamily(diff: SyncDiff | null | undefined, wanted?: string | null): SyncFamily {
  const families = diff?.families ?? []
  if (wanted && families.some((f) => f.family === wanted)) return wanted as SyncFamily
  const withWork = families.find((f) => (f.counts?.new ?? 0) + (f.counts?.different ?? 0) > 0)
  return (withWork ?? families[0])?.family ?? SYNC_FAMILIES[0]
}

/**
 * A `new` item has no changes, only its source copy: one pseudo-change per
 * top-level field, so the same FieldDiff renders it.
 */
export function sourceAsChanges(source: Record<string, unknown> | undefined | null): SyncChange[] {
  if (!source) return []
  return Object.keys(source)
    .sort()
    .map((path) => ({ path, before: null, after: source[path] }))
}

/** `{en, fr}`-shaped maps are split per locale in the field diff. */
export function isLocaleMap(v: unknown): v is Record<string, string> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const entries = Object.entries(v as Record<string, unknown>)
  return entries.length > 0 && entries.every(([k, val]) => /^[a-z]{2}(-[A-Z]{2})?$/.test(k) && typeof val === 'string')
}

export interface FieldRow {
  path: string
  before: unknown
  after: unknown
  added?: unknown[]
  removed?: unknown[]
}

/** Expands changes into the rows the field diff paints: locale maps split. */
export function fieldRows(changes: SyncChange[]): FieldRow[] {
  const rows: FieldRow[] = []
  for (const c of changes) {
    if ((c.added && c.added.length > 0) || (c.removed && c.removed.length > 0)) {
      rows.push({ path: c.path, before: c.before, after: c.after, added: c.added ?? [], removed: c.removed ?? [] })
      continue
    }
    const b = c.before
    const a = c.after
    const bLoc = b === null || b === undefined || isLocaleMap(b)
    const aLoc = a === null || a === undefined || isLocaleMap(a)
    if (bLoc && aLoc && (isLocaleMap(b) || isLocaleMap(a))) {
      const bm = (b ?? {}) as Record<string, string>
      const am = (a ?? {}) as Record<string, string>
      const locales = [...new Set([...Object.keys(bm), ...Object.keys(am)])].sort()
      for (const loc of locales) {
        if (bm[loc] === am[loc]) continue
        rows.push({ path: c.path ? `${c.path}.${loc}` : loc, before: bm[loc] ?? null, after: am[loc] ?? null })
      }
      continue
    }
    rows.push({ path: c.path, before: b, after: a })
  }
  return rows
}

/** A value painted as a block with a line diff rather than inline. */
export function isMultiline(v: unknown): boolean {
  if (typeof v === 'string') return v.includes('\n')
  if (v && typeof v === 'object') return JSON.stringify(v).length > 60
  return false
}

export function blockText(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  return JSON.stringify(v, null, 2)
}

type Translate = (key: string, opts?: Record<string, unknown>) => string

/** The source of a run as it was when it applied. */
export function runSourceLabel(run: SyncRunSummary, t: Translate): string {
  if (run.source.kind === 'defaults') return t('sync.runs.source_defaults', { version: run.source_label })
  return run.source_label
}

/** Who applied a run. */
export function runActorLabel(run: SyncRunSummary, t: Translate): string {
  return run.actor?.name || run.actor?.email || t('sync.unknown_user')
}

/**
 * The document types of an import that differ from the shipped defaults on
 * what decides their extraction jobs (spec 2026-10-14-05), sorted by code.
 */
export function driftedImportTypes(drift: SyncDrift | undefined, importCodes: string[]): SyncDriftDocumentType[] {
  if (!drift) return []
  const wanted = new Set(importCodes)
  return drift.organization.document_types.filter((dt) => wanted.has(dt.code)).sort((a, b) => a.code.localeCompare(b.code))
}

/** How many documents a confirmed apply would queue for a new extraction. */
export function reextractionDocuments(reextraction: SyncReextraction[] | undefined): number {
  return (reextraction ?? []).reduce((n, r) => n + r.documents, 0)
}
